import InvoiceFooter from "Components/Common/PdfPreview/InvoiceFooter";
import ItemsTable from "Components/Common/PdfPreview/ItemList";
import SenderAndBillingDetails from "Components/Common/PdfPreview/SenderAndBillingDetails";
import { memo, useMemo } from "react";
import { Col } from "reactstrap";
import { CompanyPayload } from "types/auth.api";
import { ClientsPayloadData } from "types/client.api";
import { ItemData } from "types/item.api";
import { SectionPayloadData } from "types/section.api";
import parse from "html-react-parser";

interface Props {
  companyDetails?: CompanyPayload | null;
  clientDetails?: ClientsPayloadData | null;
  selectedOption?: string;
  issueDate?: string;
  dueDate?: string;
  tableData?: ItemData[];
  referenceNumber?: string;
  selectedTab?: string;
  jobDescription?: string;
  allSelectedSections?: SectionPayloadData[];
  quoteTitle?: string;
  isCompanyPhoneHidden?: boolean;
  isCompanyNameHidden?: boolean;
}

const TemplateSecond = ({
  clientDetails,
  companyDetails,
  selectedOption = "items",
  dueDate,
  issueDate,
  tableData = [],
  referenceNumber,
  selectedTab,
  jobDescription,
  allSelectedSections,
  quoteTitle,
  isCompanyPhoneHidden,
  isCompanyNameHidden,
}: Props) => {
  // -----------------------
  //  COLUMNS PER VIEW TYPE
  // -----------------------
  const columns = useMemo(() => {
    switch (selectedOption) {
      case "by-item":
        return [
          {
            header: "Item",
            accessorKey: "name",
            enableColumnFilter: false,
            enableSorting: false,
            cell: (cell: any) => (
              <span className="fw-normal text-black2D fs-14 font-primary ms-1">
                {cell.getValue()}
              </span>
            ),
          },
          {
            header: "Unit",
            accessorKey: "unit",
            enableColumnFilter: false,
            enableSorting: false,
            cell: (cell: any) => (
              <span className="fw-normal text-black2D fs-14 font-primary ms-1">
                {cell.getValue()}
              </span>
            ),
          },
          {
            header: "Qty",
            accessorKey: "quantity",
            enableColumnFilter: false,
            enableSorting: false,
            cell: (cell: any) => (
              <span className="fw-normal text-black2D fs-14 font-primary ms-1">
                {cell.getValue()}
              </span>
            ),
          },
          {
            header: "Unit Price",
            accessorKey: "price",
            enableColumnFilter: false,
            enableSorting: false,
            cell: (cell: any) => {
              const value = Number(cell.getValue() ?? 0);
              return (
                <span className="fw-normal text-black2D fs-14 font-primary ms-1">
                  £{value.toFixed(2)}
                </span>
              );
            },
          },
          {
            header: "Total",
            accessorKey: "total",
            enableColumnFilter: false,
            enableSorting: false,
            cell: (cell: any) => {
              const row = cell.row.original as ItemData;
              const qty = Number(row.quantity ?? 0);
              const price = Number(row.price ?? 0);
              const total = qty * price;

              return (
                <span className="fw-normal text-black2D fs-14 font-primary ms-1">
                  £{total.toFixed(2)}
                </span>
              );
            },
          },
        ];

      case "by-category":
        return [
          {
            header: "Category",
            accessorKey: "category",
            enableColumnFilter: false,
            enableSorting: false,
            cell: (cell: any) => (
              <span className="fw-normal text-black2D fs-14 font-primary ms-1">
                {cell.getValue() ? cell.getValue() : "-"}
              </span>
            ),
          },
          {
            header: "Items",
            accessorKey: "itemsCount",
            enableColumnFilter: false,
            enableSorting: false,
            cell: (cell: any) => (
              <span className="fw-normal text-black2D fs-14 font-primary ms-1">
                {cell.getValue()}
              </span>
            ),
          },
          {
            header: "Total",
            accessorKey: "total",
            enableColumnFilter: false,
            enableSorting: false,
            cell: (cell: any) => {
              const value = Number(cell.getValue() ?? 0);
              return (
                <span className="fw-normal text-black2D fs-14 font-primary ms-1">
                  £{value.toFixed(2)}
                </span>
              );
            },
          },
        ];

      case "by-subcategory":
        return [
          {
            header: "Subcategory",
            accessorKey: "subcategory",
            enableColumnFilter: false,
            enableSorting: false,
            cell: (cell: any) => (
              <span className="fw-normal text-black2D fs-14 font-primary ms-1">
                {cell.getValue() ? cell.getValue() : "-"}
              </span>
            ),
          },
          {
            header: "Items",
            accessorKey: "itemsCount",
            enableColumnFilter: false,
            enableSorting: false,
            cell: (cell: any) => (
              <span className="fw-normal text-black2D fs-14 font-primary ms-1">
                {cell.getValue()}
              </span>
            ),
          },
          {
            header: "Total",
            accessorKey: "total",
            enableColumnFilter: false,
            enableSorting: false,
            cell: (cell: any) => {
              const value = Number(cell.getValue() ?? 0);
              return (
                <span className="fw-normal text-black2D fs-14 font-primary ms-1">
                  £{value.toFixed(2)}
                </span>
              );
            },
          },
        ];

      case "by-category-subcategory-item":
      default:
        return [
          {
            header: "Subcategory",
            accessorKey: "subcategory",
            enableColumnFilter: false,
            enableSorting: false,
            cell: (cell: any) => (
              <span className="fw-normal text-black2D fs-14 font-primary ms-1">
                {cell.getValue() ? cell.getValue() : "-"}
              </span>
            ),
          },
          {
            header: "Item",
            accessorKey: "item",
            enableColumnFilter: false,
            enableSorting: false,
            cell: (cell: any) => (
              <span className="fw-normal text-black2D fs-14 font-primary ms-1">
                {cell.getValue()}
              </span>
            ),
          },
          {
            header: "Unit",
            accessorKey: "unit",
            enableColumnFilter: false,
            enableSorting: false,
            cell: (cell: any) => (
              <span className="fw-normal text-black2D fs-14 font-primary ms-1">
                {cell.getValue()}
              </span>
            ),
          },
          {
            header: "Qty",
            accessorKey: "qty",
            enableColumnFilter: false,
            enableSorting: false,
            cell: (cell: any) => (
              <span className="fw-normal text-black2D fs-14 font-primary ms-1">
                {cell.getValue()}
              </span>
            ),
          },
          {
            header: "Unit Price",
            accessorKey: "unitPrice",
            enableColumnFilter: false,
            enableSorting: false,
            cell: (cell: any) => {
              const value = Number(cell.getValue() ?? 0);
              return (
                <span className="fw-normal text-black2D fs-14 font-primary ms-1">
                  £{value.toFixed(2)}
                </span>
              );
            },
          },
          {
            header: "Total",
            accessorKey: "total",
            enableColumnFilter: false,
            enableSorting: false,
            cell: (cell: any) => {
              const value = Number(cell.getValue() ?? 0);
              return (
                <span className="fw-normal text-black2D fs-14 font-primary ms-1">
                  £{value.toFixed(2)}
                </span>
              );
            },
          },
        ];
    }
  }, [selectedOption]);

  // -----------------------------------
  //  GROUPING / SUMMARY DATA PER MODE
  // -----------------------------------
  const { categoryViewData, subcategoryViewData, allViewData } = useMemo(() => {
    const result = {
      categoryViewData: [] as {
        category: string;
        itemsCount: number;
        total: number;
      }[],
      subcategoryViewData: [] as {
        subcategory: string;
        itemsCount: number;
        total: number;
      }[],
      allViewData: [] as {
        categoryId: string | number;
        category: string;
        rows: any[];
      }[],
    };

    if (!Array.isArray(tableData)) return result;

    const categoryMap = new Map<
      string | number,
      {
        categoryId: string | number;
        category: string;
        subMap: Map<
          string | number,
          {
            subcategoryId: string | number;
            subcategory: string;
            itemMap: Map<
              string,
              {
                name: string;
                qty: number;
                price: number;
                total: number;
                unit: string;
              }
            >;
          }
        >;
      }
    >();

    tableData.forEach((item) => {
      const categoryId = item.category_id ?? "na";
      const categoryName = item.category_name ?? "-";

      const subId = item.subcategory_id ?? "na";
      const subName = item.subcategory_name ?? "-";

      if (!categoryMap.has(categoryId)) {
        categoryMap.set(categoryId, {
          categoryId,
          category: categoryName,
          subMap: new Map(),
        });
      }

      const categoryData = categoryMap.get(categoryId)!;

      if (!categoryData.subMap.has(subId)) {
        categoryData.subMap.set(subId, {
          subcategoryId: subId,
          subcategory: subName,
          itemMap: new Map(),
        });
      }

      const subData = categoryData.subMap.get(subId)!;

      const itemKey = item.name?.toLowerCase() ?? "unknown-item";
      const qty = Number(item.quantity ?? 0);
      const price = Number(item.price ?? 0);

      if (!subData.itemMap.has(itemKey)) {
        subData.itemMap.set(itemKey, {
          name: item.name,
          qty,
          price,
          total: qty * price,
          unit: item.unit,
        });
      } else {
        const exist = subData.itemMap.get(itemKey)!;
        exist.qty += qty;
        exist.total += qty * price;
      }
    });

    const categoryArray = Array.from(categoryMap.values());

    // CATEGORIES summary
    let catTemp = categoryArray.map((cat) => {
      const subItems = Array.from(cat.subMap.values()).flatMap((sub) =>
        Array.from(sub.itemMap.values()),
      );
      return {
        category: cat.category,
        itemsCount: subItems.reduce((s, i) => s + i.qty, 0),
        total: subItems.reduce((s, i) => s + i.total, 0),
      };
    });

    // Merge - Categories
    const catNA = catTemp.filter((c) => c.category === "-");
    const catValid = catTemp.filter((c) => c.category !== "-");

    if (catNA.length > 0) {
      const merged = catNA.reduce(
        (acc, c) => {
          acc.itemsCount += c.itemsCount;
          acc.total += c.total;
          return acc;
        },
        { category: "-", itemsCount: 0, total: 0 },
      );
      result.categoryViewData = [...catValid, merged];
    } else {
      result.categoryViewData = catValid;
    }

    // SUBCATEGORY summary
    let subTemp = categoryArray.flatMap((cat) =>
      Array.from(cat.subMap.values()).map((sub) => {
        const items = Array.from(sub.itemMap.values());
        return {
          subcategory: sub.subcategory,
          itemsCount: items.reduce((s, i) => s + i.qty, 0),
          total: items.reduce((s, i) => s + i.total, 0),
        };
      }),
    );

    // Merge - Subcategories
    const subNA = subTemp.filter((s) => s.subcategory === "-");
    const subValid = subTemp.filter((s) => s.subcategory !== "-");

    if (subNA.length > 0) {
      const mergedSub = subNA.reduce(
        (acc, s) => {
          acc.itemsCount += s.itemsCount;
          acc.total += s.total;
          return acc;
        },
        { subcategory: "-", itemsCount: 0, total: 0 },
      );
      result.subcategoryViewData = [...subValid, mergedSub];
    } else {
      result.subcategoryViewData = subValid;
    }

    // ALL view (Category → Subcategory → Items)
    result.allViewData = categoryArray.map((cat) => ({
      categoryId: cat.categoryId,
      category: cat.category,
      rows: Array.from(cat.subMap.values()).flatMap((sub) =>
        Array.from(sub.itemMap.values()).map((i) => ({
          subcategory: sub.subcategory,
          item: i.name,
          qty: i.qty,
          unitPrice: i.price,
          total: i.total,
          unit: i.unit,
        })),
      ),
    }));

    return result;
  }, [tableData]);

  // decide data for non-"all" views
  const mainTableData = useMemo(() => {
    switch (selectedOption) {
      case "by-category":
        return categoryViewData;
      case "by-subcategory":
        return subcategoryViewData;
      case "by-item":
      default:
        return tableData;
    }
  }, [selectedOption, categoryViewData, subcategoryViewData, tableData]);

  const brandColor = companyDetails?.brand_color ?? "#082B60";

  return (
    <Col
      className="position-relative p-0"
      style={{
        cursor: "pointer",
        borderRadius: "12px",
        minHeight: "100vh",
        overflow: "hidden",
      }}
    >
      <svg
        viewBox={`0 0 341 ${selectedTab === "1" ? 128 : 58}`}
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="position-absolute start-0 w-100 h-auto"
        preserveAspectRatio="xMinYMin meet"
      >
        <path
          d="M6.2872e-05 0.000204206L304.227 8.09327e-06L264.627 128H5.6922e-06L6.2872e-05 0.000204206Z"
          fill="url(#paint0_linear_9885_9949)"
        />
        <path
          d="M269.364 128H271.793L311.392 0.949182H308.964L269.364 128Z"
          fill="url(#paint1_linear_9885_9949)"
        />
        <path
          d="M276.53 128H278.958L318.558 0.949182H316.13L276.53 128Z"
          fill="url(#paint2_linear_9885_9949)"
        />
        <path
          d="M283.696 128H286.124L325.724 0.949182H323.296L283.696 128Z"
          fill="url(#paint3_linear_9885_9949)"
        />
        <path
          d="M290.862 128H293.291L332.89 0.949182H330.462L290.862 128Z"
          fill="url(#paint4_linear_9885_9949)"
        />
        <path
          d="M298.028 128H300.457L340.057 0.949182H337.628L298.028 128Z"
          fill="url(#paint5_linear_9885_9949)"
        />
        <defs>
          <linearGradient
            id="paint0_linear_9885_9949"
            x1="-1.52577e-05"
            y1="128"
            x2="51.9505"
            y2="-75.5266"
            gradientUnits="userSpaceOnUse"
          >
            <stop stop-color="#F2EEEC" />
            <stop offset="1" stop-color="#E8E8F2" />
          </linearGradient>
          <linearGradient
            id="paint1_linear_9885_9949"
            x1="269.364"
            y1="128"
            x2="341.049"
            y2="64.5982"
            gradientUnits="userSpaceOnUse"
          >
            <stop stop-color="#F2EEEC" />
            <stop offset="1" stop-color="#E8E8F2" />
          </linearGradient>
          <linearGradient
            id="paint2_linear_9885_9949"
            x1="276.53"
            y1="128"
            x2="348.215"
            y2="64.5974"
            gradientUnits="userSpaceOnUse"
          >
            <stop stop-color="#F2EEEC" />
            <stop offset="1" stop-color="#E8E8F2" />
          </linearGradient>
          <linearGradient
            id="paint3_linear_9885_9949"
            x1="283.696"
            y1="128"
            x2="355.382"
            y2="64.5974"
            gradientUnits="userSpaceOnUse"
          >
            <stop stop-color="#F2EEEC" />
            <stop offset="1" stop-color="#E8E8F2" />
          </linearGradient>
          <linearGradient
            id="paint4_linear_9885_9949"
            x1="290.862"
            y1="128"
            x2="362.548"
            y2="64.5974"
            gradientUnits="userSpaceOnUse"
          >
            <stop stop-color="#F2EEEC" />
            <stop offset="1" stop-color="#E8E8F2" />
          </linearGradient>
          <linearGradient
            id="paint5_linear_9885_9949"
            x1="298.028"
            y1="128"
            x2="369.714"
            y2="64.5974"
            gradientUnits="userSpaceOnUse"
          >
            <stop stop-color="#F2EEEC" />
            <stop offset="1" stop-color="#E8E8F2" />
          </linearGradient>
        </defs>
      </svg>

      <div
        className="position-relative d-flex flex-column"
        style={{
          minHeight: "100vh", // or match your desired height
          zIndex: 1,
        }}
      >
        <svg
          className="w-100 br-tr-8"
          viewBox="0 0 176 8"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M176 -0.000221729V7.97705H26.0062L31.3925 -0.000221729H176Z"
            fill={brandColor}
          />
          <path
            d="M29.5449 -0.000221729L24.1586 7.97705H23.5082L28.8945 -0.000221729H29.5449Z"
            fill={brandColor}
          />
          <path
            d="M27.0469 -0.000221729L21.6306 7.97705H20.9799L26.3961 -0.000221729H27.0469Z"
            fill={brandColor}
          />
          <path
            d="M24.5488 -0.000205994L19.1625 7.97705H18.5118L23.8981 -0.000205994H24.5488Z"
            fill={brandColor}
          />
          <path
            d="M22.0508 -0.000221729L16.6645 7.97705H16.0138L21.4001 -0.000221729H22.0508Z"
            fill={brandColor}
          />
        </svg>

        <div className="flex-grow-1 mx-3 overflow-auto overflow-x-hidden">
          {selectedTab === "1" && (
            <>
              <SenderAndBillingDetails
                isBillingDetails={false}
                colorName={brandColor}
                companyDetails={companyDetails}
                referenceNumber={referenceNumber}
                isFromTempLate2={true}
                className="mt-4"
                quoteName={quoteTitle || ""}
                isCompanyPhoneHidden={isCompanyPhoneHidden}
                isCompanyNameHidden={isCompanyNameHidden}
              />

              {clientDetails && (
                <div className="mt-4 pt-2">
                  <SenderAndBillingDetails
                    isBillingDetails={true}
                    colorName={brandColor}
                    clientDetails={clientDetails}
                    companyDetails={companyDetails}
                    issueDate={issueDate}
                    dueDate={dueDate}
                    isBelowQuote
                    referenceNumber={referenceNumber}
                  />
                </div>
              )}

              {jobDescription && jobDescription?.length > 0 && (
                <>
                  <div className="fw-medium text-black2d font-primary mb-2 mt-4">
                    <span style={{ color: brandColor }}> Job Description</span>
                  </div>
                  <p
                    className="text-black2D font-primary fw-normal fs-9"
                    style={{ whiteSpace: "pre-wrap" }}
                  >
                    {jobDescription}
                  </p>
                </>
              )}
            </>
          )}

          {/* TABLE AREA */}

          {selectedTab === "2" && (
            <>
              <div className="fw-medium text-black2d font-primary mb-2 mt-lg-3 mt-md-3 ">
                <span style={{ color: brandColor }}>Items Summary</span>
              </div>
              {(mainTableData.length > 0 || allViewData.length > 0) && (
                <div>
                  {selectedOption === "by-category-subcategory-item" ? (
                    <>
                      {allViewData.map((cat) => (
                        <div key={cat.categoryId} className="mt-5">
                          {/* Category heading like screenshot (MATERIAL, SERVICES, etc.) */}
                          <div
                            className="fw-semibold text-uppercase fs-14 "
                            style={{ color: brandColor }}
                          >
                            {cat.category}
                          </div>

                          <ItemsTable
                            colorName={brandColor}
                            columns={columns}
                            data={cat.rows}
                          />
                        </div>
                      ))}
                    </>
                  ) : (
                    <div className="mt-5">
                      <ItemsTable
                        colorName={brandColor}
                        columns={columns}
                        data={mainTableData}
                      />
                    </div>
                  )}

                  <InvoiceFooter colorName={brandColor} />
                </div>
              )}
            </>
          )}

          {selectedTab === "3" && (
            <>
              <div className="fw-medium text-black2d font-primary mb-2 mt-3">
                <span style={{ color: brandColor }}>Quote Summary</span>
              </div>
              <div className="mt-5" />

              {allSelectedSections &&
                allSelectedSections.length > 0 &&
                allSelectedSections.map((section, index) => (
                  <div key={section.id} className="mb-4">
                    <div
                      className="fw-semibold text-uppercase fs-14 mb-2"
                      style={{ color: brandColor }}
                    >
                      {index + 1}. {section.title}
                    </div>
                    <div className="html-content">
                      {parse(section.content) ?? "-"}
                    </div>
                  </div>
                ))}
            </>
          )}
        </div>

        <svg
          className="w-100 br-bl-8 mt-3"
          viewBox="0 0 176 8"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M0 7.93339V0H148.612L143.276 7.93339H0Z" fill={brandColor} />
          <path
            d="M145.106 7.93339L150.443 0H151.088L145.751 7.93339H145.106Z"
            fill={brandColor}
          />
          <path
            d="M147.581 7.93339L152.947 0H153.592L148.226 7.93339H147.581Z"
            fill={brandColor}
          />
          <path
            d="M150.057 7.93337L155.393 0H156.038L150.701 7.93337H150.057Z"
            fill={brandColor}
          />
          <path
            d="M152.531 7.93339L157.868 0H158.513L153.176 7.93339H152.531Z"
            fill={brandColor}
          />
        </svg>
      </div>

      <svg
        width="209"
        height="58"
        viewBox="0 0 209 58"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="position-absolute end-0"
        style={{ bottom: 0 }}
      >
        <path
          d="M208.714 57.0959L35.7981 57.096L75.3628 0H208.714L208.714 57.0959Z"
          fill="url(#paint0_linear_9885_10033)"
        />
        <path
          d="M70.6291 0H68.2031L28.639 56.543H31.065L70.6291 0Z"
          fill="url(#paint1_linear_9885_10033)"
        />
        <path
          d="M63.4701 0H61.044L21.4794 56.543H23.9054L63.4701 0Z"
          fill="url(#paint2_linear_9885_10033)"
        />
        <path
          d="M56.3102 0H53.8842L14.3195 56.543H16.7456L56.3102 0Z"
          fill="url(#paint3_linear_9885_10033)"
        />
        <path
          d="M49.1506 0H46.724L7.1599 56.543H9.58594L49.1506 0Z"
          fill="url(#paint4_linear_9885_10033)"
        />
        <path
          d="M41.9908 0H39.5647L4.57764e-05 56.543H2.42609L41.9908 0Z"
          fill="url(#paint5_linear_9885_10033)"
        />
        <defs>
          <linearGradient
            id="paint0_linear_9885_10033"
            x1="208.714"
            y1="2.66129e-06"
            x2="177.818"
            y2="100.679"
            gradientUnits="userSpaceOnUse"
          >
            <stop stop-color="#F2EEEC" />
            <stop offset="1" stop-color="#E8E8F2" />
          </linearGradient>
          <linearGradient
            id="paint1_linear_9885_10033"
            x1="70.6291"
            y1="2.63552e-06"
            x2="17.4268"
            y2="42.5112"
            gradientUnits="userSpaceOnUse"
          >
            <stop stop-color="#F2EEEC" />
            <stop offset="1" stop-color="#E8E8F2" />
          </linearGradient>
          <linearGradient
            id="paint2_linear_9885_10033"
            x1="63.4701"
            y1="2.63552e-06"
            x2="10.2676"
            y2="42.5119"
            gradientUnits="userSpaceOnUse"
          >
            <stop stop-color="#F2EEEC" />
            <stop offset="1" stop-color="#E8E8F2" />
          </linearGradient>
          <linearGradient
            id="paint3_linear_9885_10033"
            x1="56.3103"
            y1="2.63552e-06"
            x2="3.1078"
            y2="42.5119"
            gradientUnits="userSpaceOnUse"
          >
            <stop stop-color="#F2EEEC" />
            <stop offset="1" stop-color="#E8E8F2" />
          </linearGradient>
          <linearGradient
            id="paint4_linear_9885_10033"
            x1="49.1506"
            y1="2.63552e-06"
            x2="-4.05182"
            y2="42.5119"
            gradientUnits="userSpaceOnUse"
          >
            <stop stop-color="#F2EEEC" />
            <stop offset="1" stop-color="#E8E8F2" />
          </linearGradient>
          <linearGradient
            id="paint5_linear_9885_10033"
            x1="41.9908"
            y1="2.63552e-06"
            x2="-11.2117"
            y2="42.5119"
            gradientUnits="userSpaceOnUse"
          >
            <stop stop-color="#F2EEEC" />
            <stop offset="1" stop-color="#E8E8F2" />
          </linearGradient>
        </defs>
      </svg>
    </Col>
  );
};

export default memo(TemplateSecond);
